import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
    firestore,
    doc,
    collection,
    addDoc,
    query,
    orderBy,
    onSnapshot,
    updateDoc,
} from "./firebase";
import { Card, Button, Form, Row, Col, Spinner, Image, OverlayTrigger, Tooltip } from "react-bootstrap";
import { arrayUnion, arrayRemove, increment, deleteDoc } from "firebase/firestore";

const COMMENT_CHAR_LIMIT = 100;
// Replace with your Cloud Function URL for comment notifications
const SLACK_FUNCTION_URL = "https://us-central1-team-ideas.cloudfunctions.net/sendSlackNotification";

async function sendSlackNotification(message) {
    try {
        const response = await fetch(SLACK_FUNCTION_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message }),
        });
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
    } catch (error) {
        console.error("Error sending Slack notification via cloud function:", error);
    }
}

function IdeaDetail({ user }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [idea, setIdea] = useState(null);
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isVoting, setIsVoting] = useState(false);
    const [commentVoting, setCommentVoting] = useState({});
    const [commentDeleting, setCommentDeleting] = useState({});
    const [shareSuccess, setShareSuccess] = useState(false);

    useEffect(() => {
        const ideaRef = doc(firestore, "ideas", id);
        const unsubscribeIdea = onSnapshot(ideaRef, (docSnapshot) => {
            if (docSnapshot.exists())
                setIdea({ id: docSnapshot.id, ...docSnapshot.data() });
        });

        const commentsQuery = query(
            collection(firestore, "ideas", id, "comments"),
            orderBy("createdAt", "asc")
        );
        const unsubscribeComments = onSnapshot(commentsQuery, (snapshot) => {
            const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
            setComments(data);
        });

        setLoading(false);

        return () => {
            unsubscribeIdea();
            unsubscribeComments();
        };
    }, [id]);

    const handleBack = () => {
        navigate("/");
    };

    const handleShare = async () => {
        try {
            await navigator.clipboard.writeText(window.location.href);
            setShareSuccess(true);
            setTimeout(() => {
                setShareSuccess(false);
            }, 3000);
        } catch (err) {
            console.error("Failed to copy: ", err);
        }
    };

    const handleDeleteIdea = async () => {
        if (!window.confirm("Are you sure you want to delete this idea?")) return;
        try {
            await deleteDoc(doc(firestore, "ideas", id));
            navigate("/");
        } catch (error) {
            console.error("Error deleting idea:", error);
        }
    };

    const handleVote = async () => {
        if (user.uid === idea.uid)
            return alert("You cannot vote for your own idea!");
        if (isVoting) return;
        setIsVoting(true);
        // Check if the current user has already voted by looking for an object with matching uid.
        const alreadyVoted = idea.voters && idea.voters.some(v => v.uid === user.uid);
        const ideaRef = doc(firestore, "ideas", id);
        try {
            if (alreadyVoted) {
                await updateDoc(ideaRef, {
                    votesCount: idea.votesCount - 1,
                    voters: arrayRemove({ uid: user.uid, userName: user.displayName, userAvatar: user.photoURL }),
                });
            } else {
                await updateDoc(ideaRef, {
                    votesCount: idea.votesCount + 1,
                    voters: arrayUnion({ uid: user.uid, userName: user.displayName, userAvatar: user.photoURL }),
                });
            }
        } catch (error) {
            console.error("Error voting on idea:", error);
        } finally {
            setIsVoting(false);
        }
    };

    const handleSubmitComment = async (e) => {
        e.preventDefault();
        if (!commentText.trim() || isSubmitting) return;

        setIsSubmitting(true);

        try {
            await addDoc(collection(firestore, "ideas", id, "comments"), {
                text: commentText,
                uid: user.uid,
                userName: user.displayName,
                userAvatar: user.photoURL,
                createdAt: new Date(),
                votesCount: 0,
                voters: [],
            });
            // Increment the parent idea's comment count
            const ideaRef = doc(firestore, "ideas", id);
            await updateDoc(ideaRef, {
                commentsCount: increment(1),
            });
            // Build the idea link and send a formal Slack notification for the comment
            const ideaLink = `${window.location.origin}/idea/${id}`;
            const message = `🔔 New Comment Submission: ${user.displayName} has submitted a comment on an idea. Please review the idea here: ${ideaLink} and consider providing your feedback.`;
            sendSlackNotification(message);
            setCommentText("");
        } catch (error) {
            console.error("Error adding comment:", error);
        } finally {
            setIsSubmitting(false);
        }
    };


    const handleCommentVote = async (comment) => {
        if (comment.uid === user.uid) return;
        if (commentVoting[comment.id]) return;
        setCommentVoting((prev) => ({ ...prev, [comment.id]: true }));
        const alreadyVoted = comment.voters && comment.voters.some(v => v.uid === user.uid);
        const commentRef = doc(firestore, "ideas", id, "comments", comment.id);
        try {
            if (alreadyVoted) {
                await updateDoc(commentRef, {
                    votesCount: comment.votesCount - 1,
                    voters: arrayRemove({ uid: user.uid, userName: user.displayName, userAvatar: user.photoURL }),
                });
            } else {
                await updateDoc(commentRef, {
                    votesCount: comment.votesCount + 1,
                    voters: arrayUnion({ uid: user.uid, userName: user.displayName, userAvatar: user.photoURL }),
                });
            }
        } catch (error) {
            console.error("Error voting on comment:", error);
        } finally {
            setCommentVoting((prev) => ({ ...prev, [comment.id]: false }));
        }
    };

    const handleDeleteComment = async (comment) => {
        if (!window.confirm("Are you sure you want to delete this comment?")) return;
        setCommentDeleting((prev) => ({ ...prev, [comment.id]: true }));
        try {
            const commentRef = doc(firestore, "ideas", id, "comments", comment.id);
            await deleteDoc(commentRef);
            const ideaRef = doc(firestore, "ideas", id);
            await updateDoc(ideaRef, {
                commentsCount: increment(-1),
            });
        } catch (error) {
            console.error("Error deleting comment:", error);
        } finally {
            setCommentDeleting((prev) => ({ ...prev, [comment.id]: false }));
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "300px" }}>
                <Spinner animation="border" variant="primary" />
            </div>
        );
    }

    return (
        <div className="container my-4" style={{ maxWidth: "800px" }}>
            <Card className="p-4">
                {idea && (
                    <>
                        {/* Back, Share, and (if owner) Delete Buttons */}
                        <div className="mb-3 d-flex justify-content-between">
                            <Button variant="secondary" size="sm" onClick={handleBack}>
                                ← Back
                            </Button>
                            <div className="d-flex gap-2">
                                <Button variant="info" size="sm" onClick={handleShare}>
                                    Share
                                </Button>
                                {idea.uid === user.uid && (
                                    <Button variant="danger" size="sm" onClick={handleDeleteIdea}>
                                        Delete
                                    </Button>
                                )}
                                {shareSuccess && <span className="ms-2 text-success">Link copied!</span>}
                            </div>
                        </div>

                        <h2>{idea.text}</h2>
                        <div className="mb-3 d-flex justify-content-between">
                            <p className="text-muted">Posted by {idea.userName}</p>
                            <small className="text-muted">
                                {idea.createdAt.toDate().toLocaleString()}
                            </small>
                        </div>
                        <Button
                            onClick={handleVote}
                            variant={idea.voters && idea.voters.some(v => v.uid === user.uid) ? "outline-danger" : "outline-primary"}
                            disabled={user.uid === idea.uid || isVoting}
                            className="mb-3"
                            style={{ width: "auto" }}
                        >
                            {idea.voters && idea.voters.some(v => v.uid === user.uid)
                                ? `Unvote (${idea.votesCount})`
                                : `Vote (${idea.votesCount})`}
                        </Button>

                        {/* Display voters as small avatars with tooltips */}
                        {idea.voters && idea.voters.length > 0 && (
                            <div className="d-flex align-items-center mb-3">
                                <small className="me-2">Voted by:</small>
                                {idea.voters.map((voter, index) => (
                                    <OverlayTrigger
                                        key={index}
                                        placement="top"
                                        overlay={<Tooltip id={`tooltip-${voter.uid}`}>{voter.userName}</Tooltip>}
                                    >
                                        <Image
                                            src={voter.userAvatar}
                                            roundedCircle
                                            width="30"
                                            height="30"
                                            className="me-1"
                                        />
                                    </OverlayTrigger>
                                ))}
                            </div>
                        )}

                        <p className="text-muted">Total Comments: {idea.commentsCount || 0}</p>

                        <Form onSubmit={handleSubmitComment} className="mt-4">
                            <Form.Group controlId="commentInput">
                                <Form.Label>💬 Add a Comment</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    maxLength={COMMENT_CHAR_LIMIT}
                                    placeholder="Write your comment..."
                                    value={commentText}
                                    onChange={(e) => setCommentText(e.target.value)}
                                    disabled={isSubmitting}
                                />
                                <div className="text-end text-muted mt-1">
                                    {commentText.length} / {COMMENT_CHAR_LIMIT} characters
                                </div>
                            </Form.Group>
                            <Button type="submit" variant="primary" disabled={!commentText.trim() || isSubmitting} className="mt-2">
                                {isSubmitting ? "Submitting..." : "Post Comment"}
                            </Button>
                        </Form>

                        <div className="mt-5">
                            <h4>Comments ({idea.commentsCount || comments.length})</h4>
                            {comments.length === 0 ? (
                                <p className="text-muted">No comments yet. Be the first to comment!</p>
                            ) : (
                                comments.map((comment) => (
                                    <Card key={comment.id} className="mt-3 p-3">
                                        <Row className="align-items-center">
                                            <Col xs="auto">
                                                <Image
                                                    src={comment.userAvatar}
                                                    roundedCircle
                                                    width={40}
                                                    height={40}
                                                    alt={comment.userName}
                                                />
                                            </Col>
                                            <Col>
                                                <strong>{comment.userName}</strong>
                                                <p className="mb-1">{comment.text}</p>
                                                <small className="text-muted">
                                                    {comment.createdAt.toDate().toLocaleString()}
                                                </small>
                                            </Col>
                                            <Col xs="auto" className="d-flex flex-column align-items-end">
                                                {comment.uid !== user.uid ? (
                                                    <Button
                                                        variant={comment.voters && comment.voters.some(v => v.uid === user.uid) ? "outline-danger" : "outline-primary"}
                                                        size="sm"
                                                        onClick={() => handleCommentVote(comment)}
                                                        disabled={commentVoting[comment.id]}
                                                        className="mb-1"
                                                    >
                                                        {comment.voters && comment.voters.some(v => v.uid === user.uid)
                                                            ? `Unvote (${comment.votesCount || 0})`
                                                            : `Vote (${comment.votesCount || 0})`}
                                                    </Button>
                                                ) : (
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleDeleteComment(comment)}
                                                        disabled={commentDeleting[comment.id]}
                                                    >
                                                        Delete
                                                    </Button>
                                                )}
                                            </Col>
                                        </Row>
                                    </Card>
                                ))
                            )}
                        </div>
                    </>
                )}
            </Card>
        </div>
    );
}

export default IdeaDetail;
